import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  ArrowLeft, FileText, BookOpen, Clock, Target, ChevronRight, 
  CheckCircle, Lock, Trophy, Star, Sparkles, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaperLibrary = ({ progress }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stage, subject } = useParams();
  const [searchParams] = useSearchParams();
  // When BoardPracticeBrowser deep-links here with ?paper=N&returnTo=…
  // we auto-launch the paper and stash the return path so the test
  // page's exit button can drop the student back into the Board → Subject
  // section instead of the legacy mock catalogue.
  const deepLinkPaper = parseInt(searchParams.get('paper') || '0', 10);
  const deepLinkReturnTo = searchParams.get('returnTo') || '';
  
  // Determine where back button should go
  const getBackDestination = () => {
    if (deepLinkReturnTo) return deepLinkReturnTo;
    // If came from A-Level Hub (via state or stage is a-levels)
    if (location.state?.fromALevelHub || stage === 'a-levels') {
      return '/a-level-hub';
    }
    // Default: go to subject dashboard for the stage
    return `/subjects/${stage}`;
  };
  const [paperProgress, setPaperProgress] = useState(null);
  const [customBanks, setCustomBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('practice');

  useEffect(() => {
    loadAllData();
  }, [stage, subject]);

  // Auto-launch the deep-link paper once progress is loaded. Without this,
  // a student clicking 'GL → English → Paper 2' on the BoardPracticeBrowser
  // would land on the paper-listing screen and have to find Paper 2 again.
  useEffect(() => {
    if (deepLinkPaper >= 1 && !loading && paperProgress) {
      startPaper('practice', deepLinkPaper);
    }
  }, [deepLinkPaper, loading, paperProgress]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load paper progress and custom banks in parallel
      const [progressRes, customRes] = await Promise.all([
        axios.get(`${API}/papers/progress/${stage}/${subject}`, { headers }).catch(() => null),
        axios.get(`${API}/custom-papers/${stage}/${subject}`, { headers }).catch(() => ({ data: { customBanks: [] } }))
      ]);

      if (progressRes?.data) {
        setPaperProgress(progressRes.data);
      } else {
        setPaperProgress({
          mock: { unlocked: 2, completed: {}, total: 20 },
          practice: { unlocked: 2, completed: {}, total: 20 }
        });
      }

      setCustomBanks(customRes?.data?.customBanks || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setPaperProgress({
        mock: { unlocked: 2, completed: {}, total: 20 },
        practice: { unlocked: 2, completed: {}, total: 20 }
      });
    } finally {
      setLoading(false);
    }
  };

  const startPaper = (paperType, paperNumber) => {
    const progressData = paperProgress?.[paperType];
    if (progressData && paperNumber > progressData.unlocked) {
      toast.error(`Complete Paper ${progressData.unlocked} first to unlock this paper!`);
      return;
    }
    navigate(`/paper/${stage}/${subject}/${paperType}/${paperNumber}`, {
      state: { returnTo: deepLinkReturnTo || undefined }
    });
  };

  const startCustomPaper = (bankId, bankName) => {
    navigate(`/paper/${stage}/${subject}/custom/${bankId}`, { 
      state: { bankName, isCustom: true } 
    });
  };

  const getStageTitle = () => {
    const titles = {
      '7plus': '7+ Entry Exams',
      'seven-plus': '7+ Entry Exams',
      '11plus': '11+ Entry Exams',
      'eleven-plus': '11+ Entry Exams',
      'year-9': 'Year 9',
      'year-10': 'Year 10',
      'gcse': 'GCSE',
      'a-levels': 'A-Levels'
    };
    return titles[stage] || stage;
  };

  const renderPaperCard = (paperNum, paperType, isLocked, isCompleted, score) => {
    const bgColor = isCompleted ? 'bg-green-600' : (paperType === 'mock' ? 'bg-purple-600' : 'bg-green-600');
    const borderColor = paperType === 'mock' ? 'hover:border-purple-400' : 'hover:border-green-400';
    const label = paperType === 'mock' ? 'Mock' : 'Practice';
    
    return (
      <Card
        key={paperNum}
        className={`transition-all border-2 ${
          isLocked 
            ? 'opacity-60 cursor-not-allowed bg-gray-50' 
            : isCompleted
            ? 'bg-green-50 border-green-300 hover:shadow-lg'
            : `hover:shadow-lg cursor-pointer ${borderColor}`
        }`}
        onClick={() => !isLocked && !isCompleted && startPaper(paperType, paperNum)}
        data-testid={`${paperType}-paper-${paperNum}`}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className={`${isLocked ? 'bg-gray-400' : bgColor} text-white rounded-lg w-14 h-14 flex items-center justify-center font-bold text-xl relative`}>
              {isLocked ? <Lock className="h-6 w-6" /> : paperNum}
              {isCompleted && (
                <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1">
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
              )}
            </div>
            {isCompleted ? (
              <Badge className="bg-green-100 text-green-700 border-green-300">
                Completed
              </Badge>
            ) : isLocked ? (
              <Lock className="h-6 w-6 text-gray-400" />
            ) : (
              <ChevronRight className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <h3 className={`font-bold text-lg mb-2 ${isLocked ? 'text-gray-400' : ''}`}>
            {label} Paper {paperNum}
          </h3>
          {isCompleted && score !== undefined && (
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="h-4 w-4 text-yellow-500" />
              <span className={`font-semibold ${score >= 70 ? 'text-green-600' : 'text-orange-500'}`}>
                Best: {score}%
              </span>
            </div>
          )}
          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              <span>{paperType === 'mock' ? 'Timed exam conditions' : 'Untimed practice'}</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span>Step-by-step solutions</span>
            </div>
          </div>
          {isLocked && (
            <div className="mt-3 text-xs text-gray-500 bg-gray-100 rounded px-2 py-1">
              Complete previous papers to unlock
            </div>
          )}
          {isCompleted && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                startPaper(paperType, paperNum);
              }}
              className={`w-full mt-4 ${paperType === 'mock' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
              size="sm"
              data-testid={`redo-${paperType}-${paperNum}`}
            >
              <Target className="h-4 w-4 mr-2" />
              Re-do Paper
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderProgressHeader = (paperType) => {
    const data = paperProgress?.[paperType] || { unlocked: 2, completed: {}, total: 20 };
    const completedData = data.completed || {};
    const completedCount = Object.keys(completedData).length;
    const progressPercent = (completedCount / data.total) * 100;
    
    return (
      <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            <span className="font-semibold text-gray-700">Your Progress</span>
          </div>
          <Badge variant="outline" className="bg-white">
            {completedCount} / {data.total} completed
          </Badge>
        </div>
        <Progress value={progressPercent} className="h-2 mb-2" />
        <div className="flex justify-between text-sm text-gray-600">
          <span>{data.unlocked} papers unlocked</span>
          <span>{data.total - data.unlocked} papers locked</span>
        </div>
        {completedCount > 0 && completedCount < data.unlocked && (
          <p className="text-sm text-purple-600 mt-2">
            Complete {data.unlocked - completedCount} more paper(s) to unlock the next set!
          </p>
        )}
      </div>
    );
  };

  const renderCustomBankCard = (bank) => {
    return (
      <Card
        key={bank.id}
        className="transition-all border-2 hover:shadow-lg cursor-pointer hover:border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50"
        onClick={() => startCustomPaper(bank.id, bank.name)}
        data-testid={`custom-bank-${bank.id}`}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-lg w-14 h-14 flex items-center justify-center">
              <Sparkles className="h-7 w-7" />
            </div>
            <ChevronRight className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="font-bold text-lg mb-2 text-gray-800">{bank.name}</h3>
          {bank.description && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{bank.description}</p>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FileText className="h-4 w-4" />
            <span>{bank.question_count} questions</span>
          </div>
          <Badge className="mt-3 bg-amber-100 text-amber-700 hover:bg-amber-200">
            Custom Bank
          </Badge>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Loading papers...</p>
        </div>
      </div>
    );
  }

  const papers = Array.from({ length: paperProgress?.practice?.total || 20 }, (_, i) => i + 1);
  const hasCustomBanks = customBanks.length > 0;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-6 px-4 md:px-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(getBackDestination())}
            className="text-white hover:bg-white/20 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
          </Button>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            {getStageTitle()} - {subject.replace(/-/g, ' ').toUpperCase()}
          </h1>
          <p className="text-white/90">
            Complete papers in order to unlock more. Start with Paper 1 and progress steadily!
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
          <TabsList className={`grid w-full ${hasCustomBanks ? 'grid-cols-2' : 'grid-cols-1'} mb-6`}>
            <TabsTrigger value="practice" className="text-lg" data-testid="tab-practice">
              <BookOpen className="mr-2 h-5 w-5" />
              Practice Papers
            </TabsTrigger>
            {hasCustomBanks && (
              <TabsTrigger value="custom" className="text-lg" data-testid="tab-custom">
                <Sparkles className="mr-2 h-5 w-5" />
                Custom Banks ({customBanks.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="practice" className="space-y-4">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-6 w-6 text-green-600" />
                  Practice Papers with Solutions
                </CardTitle>
                <CardDescription>
                  Untimed practice with instant feedback and detailed step-by-step solutions for every question.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {renderProgressHeader('practice')}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {papers.map((paperNum) => {
                    const data = paperProgress?.practice || { unlocked: 2, completed: {} };
                    const isLocked = paperNum > data.unlocked;
                    const completedData = data.completed || {};
                    // Check both string and number keys for completion
                    const isCompleted = (paperNum.toString() in completedData) || (paperNum in completedData);
                    const score = completedData[paperNum.toString()] || completedData[paperNum];
                    return renderPaperCard(paperNum, 'practice', isLocked, isCompleted, score);
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {hasCustomBanks && (
            <TabsContent value="custom" className="space-y-4">
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-6 w-6 text-amber-500" />
                    Custom Question Banks
                  </CardTitle>
                  <CardDescription>
                    Additional practice materials created by your tutors and administrators.
                    These are specially curated question sets for enhanced learning.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {customBanks.map((bank) => renderCustomBankCard(bank))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default PaperLibrary;
